import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@pella/sash-framework';
import PTIHCalendarHeader from './header';
import PTIHCalendarDay from './day';
import { MONTHS } from '../../../../utilities/constants';

function PrevArrow(props) {
	// eslint-disable-next-line react/prop-types -- fix this
	const { onClick, disabled } = props;
	return (
		<button
			type='button'
			className={`${disabled ? 'hidden' : 'flex'} mr-6`}
			disabled={disabled ? true : false}
			onClick={onClick}
			aria-label='Previous Month'
		>
			<Icon
				name='keyboard_arrow_left'
				size={37}
			/>
		</button>
	);
}

function NextArrow(props) {
	// eslint-disable-next-line react/prop-types -- fix this
	const { onClick, disabled } = props;
	return (
		<button
			type='button'
			className={`${disabled ? 'hidden' : 'flex'} ml-6`}
			disabled={disabled ? true : false}
			onClick={onClick}
			aria-label='Next Month'
		>
			<Icon
				name='keyboard_arrow_right'
				size={37}
			/>
		</button>
	);
}

const PTIHCalendar = ({ timeslots, handleSelect, selectedDay, disabled }) => {
	const [month, setMonth] = useState(new Date().getMonth());
	const [year, setYear] = useState(new Date().getFullYear());
	const [timeslotObject, setTimeslotObject] = useState({});

	useEffect(() => {
		setTimeslotObject(showCalendar(month, year, timeslots));
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [timeslots]);

	const showCalendar = (month, year, timeslots, depth = 0) => {
		if (depth > 4) {
			// Limit to 4 months worth of calendar rendering
			return { Calendar: [], CurrentMonth: MONTHS[month], CurrentYear: year };
		}

		const calendarMonth = {};
		const weeks = [];
		let hasPreviousMonth = false;
		let hasNextMonth = false;
		let maxRows = 5;
		let currentDay = 1;
		let timeslotPosition = 0;

		const firstDay = new Date(year, month).getDay();
		const daysInMonth = 32 - new Date(year, month, 32).getDate();

		if (month === 1 && firstDay === 0 && daysInMonth < 29) {
			// Case that February only has 4 rows
			maxRows = 4;
		} else if ((firstDay > 4 && daysInMonth === 31) || (firstDay === 6 && daysInMonth === 30)) {
			// Case of month with 31 days with first day of month on Friday or Saturday OR a month that has 30 days with the first day on Saturday
			maxRows = 6;
		}

		const firstTimeslotMonth = parseInt(timeslots[0].startDateTime.split('T')[0].split('-')[1]) - 1;
		const lastTimeslotMonth = parseInt(timeslots[timeslots.length - 1].startDateTime.split('T')[0].split('-')[1]) - 1;

		if (firstTimeslotMonth < month || (firstTimeslotMonth === 11 && month === 0)) {
			hasPreviousMonth = true;
		}
		if (lastTimeslotMonth > month || (lastTimeslotMonth === 0 && month === 11)) {
			hasNextMonth = true;
		}

		for (let row = 0; row < maxRows; row++) {
			const calendarWeek = {};
			const days = [];

			for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
				const calendarDay = { Empty: true };

				// set blank blocks in calendar for beginning and end of month
				if ((row === 0 && dayOfWeek < firstDay) || currentDay > daysInMonth) {
					days.push(calendarDay);
				} else {
					let hasTimeslots = false;
					let timeslotLength = 0;
					let arrayStartPosition = -1;

					calendarDay.Empty = false;
					calendarDay.innerText = currentDay;

					for (let k = timeslotPosition; k < timeslots.length; k++) {
						const day = timeslots[k].startDateTime.split('T')[0].split('-')[2];
						const timeslotMonth = timeslots[k].startDateTime.split('T')[0].split('-')[1];
						const timeslotYear = timeslots[k].startDateTime.split('T')[0].split('-')[0];

						if (parseInt(day) === currentDay && month === parseInt(timeslotMonth) - 1 && year === parseInt(timeslotYear)) {
							hasTimeslots = true;
							timeslotPosition = k;
							timeslotLength++;

							if (arrayStartPosition === -1) {
								arrayStartPosition = timeslotPosition;
							}
						}
					}

					calendarDay.arrayStartPosition = arrayStartPosition;
					calendarDay.timeslotLength = timeslotLength;
					calendarDay.hasTimeslots = hasTimeslots;

					days.push(calendarDay);

					currentDay++;
				}
			}

			calendarWeek.week = days;
			weeks.push(calendarWeek);
		}

		calendarMonth.Calendar = weeks;
		calendarMonth.CurrentMonth = MONTHS[month];
		calendarMonth.CurrentYear = year;
		calendarMonth.hasNextMonth = hasNextMonth;
		calendarMonth.hasPreviousMonth = hasPreviousMonth;

		for (const week of calendarMonth.Calendar) {
			for (const day of week.week) {
				if (!day.Empty && day.timeslotLength) {
					return calendarMonth;
				}
			}
		}

		if (month === 11) {
			return showCalendar(0, year + 1, timeslots, depth + 1);
		} else {
			return showCalendar(month + 1, year, timeslots, depth + 1);
		}
	};

	const jumpToNextMonth = (currentMonth, currentYear) => {
		const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
		const nextMonth = (currentMonth + 1) % 12;
		setMonth(nextMonth);
		setYear(nextYear);
		setTimeslotObject(showCalendar(nextMonth, nextYear, timeslots));
		selectDay(0, null, null);
	};

	const jumpToPreviousMonth = (currentMonth, currentYear) => {
		const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
		const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
		setMonth(previousMonth);
		setYear(previousYear);
		setTimeslotObject(showCalendar(previousMonth, previousYear, timeslots));
		selectDay(0, null, null);
	};

	const selectDay = (id, arrayStartPosition, timeslotLength) => {
		handleSelect(arrayStartPosition, timeslotLength, id);
	};

	return (
		timeslots && (
			<div className='w-full xl:mx-10'>
				<div className='mb-4 flex items-center justify-center'>
					<PrevArrow
						disabled={disabled || !timeslotObject.hasPreviousMonth}
						onClick={() => {
							jumpToPreviousMonth(month, year);
						}}
					/>
					<span className='text-subtitle-2'>
						{timeslotObject.CurrentMonth} {timeslotObject.CurrentYear}
					</span>
					<NextArrow
						disabled={disabled || !timeslotObject.hasNextMonth}
						onClick={() => {
							jumpToNextMonth(month, year);
						}}
					/>
				</div>

				<PTIHCalendarHeader />

				{timeslotObject?.Calendar?.map((Calendar, index) => (
					<div
						className='mb-0.5 grid grid-cols-7 gap-x-0.5'
						key={`row${index}`}
					>
						{Calendar.week.map((week, index2) => (
							<PTIHCalendarDay
								id={week.innerText}
								key={`row${index}day${index2}`}
								selected={selectedDay === week.innerText ? true : false}
								onClick={selectDay}
								innerText={week.innerText}
								empty={week.Empty}
								hasTimeslots={week.hasTimeslots}
								arrayStartPosition={week.arrayStartPosition}
								timeslotLength={week.timeslotLength}
								disabled={disabled}
							/>
						))}
					</div>
				))}
			</div>
		)
	);
};

PTIHCalendar.propTypes = {
	timeslots: PropTypes.array.isRequired,
	handleSelect: PropTypes.func.isRequired,
	selectedDay: PropTypes.number,
	disabled: PropTypes.bool,
};

export default PTIHCalendar;
